import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { classNames } from '@/lib/format';
import { Tooltip } from './Tooltip';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', block, leftIcon, rightIcon, className, children, type, title, disabled, ...rest },
  ref,
) {
  const btn = (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled}
      className={classNames(
        'btn',
        `btn--${variant}`,
        size !== 'md' && `btn--${size}`,
        block && 'btn--block',
        className,
      )}
      // Kept as the ACCESSIBLE NAME even though the visible hover is the
      // styled Tooltip below. A screen reader announces aria-label; it does not
      // read a Radix tooltip that never opens, because a tooltip is a pointer
      // affordance. Only set when there are no visible children to name the
      // button -- otherwise it would override the label the user can read.
      aria-label={title && !children ? title : undefined}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );

  if (!title) return btn;

  // `title` on a Button renders as OUR tooltip, not the browser's.
  //
  // Roughly a dozen call sites pass `title` as a hover hint. Left as a native
  // attribute each one is a white-on-dark browser tooltip with its own delay,
  // sitting next to styled tooltips on the same screen. Handling it here means
  // every existing site is fixed at once and a future `title=` cannot
  // reintroduce the native one by accident.
  //
  // `wrap` is not optional here: a disabled button emits no pointer events, so
  // an `asChild` trigger would never fire -- and a large share of these titles
  // exist precisely to explain why a button is disabled ("Results already match
  // these filters"). The wrapper receives the hover instead.
  return (
    <Tooltip content={title} wrap>
      {btn}
    </Tooltip>
  );
});
